import Sidebar from "@/components/Sidebar/SidebarView.vue";
import { get_token, get_user_details } from "@/router";
import Swal from "sweetalert2";
import { view_profile_product } from "@/views/Marketplace/marketplace_helpers";
import Navbar from "@/components/HomeNavBar/HomeNavBar.vue";

export default {
  name: "ManageServicesView",
  components: {
    Navbar,
    Sidebar,
  },
  data() {
    return ({
      token_exists: null,
      user_details: { user_role: "" },
      is_loading_services: false,
      services: [],
      service_dashboard: {},
      service_dashboard_filter: "all",
    });
  },
  computed: {
    is_admin() {
      return this.token_exists &&
        ["admin", "organization admin"].includes(this.user_details?.user_role);
    },
    filtered_services() {
      return this.filtered_dashboard_services;
    },
    filtered_dashboard_services() {
      const filters = {
        active: service => this.is_active_service(service),
        inactive: service => !this.is_active_service(service),
        published: service => this.is_published_service(service),
        unpublished: service => !this.is_published_service(service),
        with_reviews: service => this.service_has_reviews(service),
        recent_30: service => this.service_created_within_days(service, 30),
      };
      const filter = filters[this.service_dashboard_filter];

      return filter ? (this.services || []).filter(filter) : this.services || [];
    },
    service_dashboard_cards() {
      return [
        { label: "Total services", value: this.get_dashboard_first(["total_count"], this.services.length), icon: "bi-briefcase-fill", tone: "primary", filter: "all" },
        { label: "Published", value: this.get_dashboard_first(["published_count"], this.local_published_count), icon: "bi-send-check-fill", tone: "success", filter: "published" },
        { label: "Unpublished", value: this.get_dashboard_first(["unpublished_count"], this.local_unpublished_count), icon: "bi-send-x-fill", tone: "secondary", filter: "unpublished" },
        { label: "With reviews", value: this.get_dashboard_first(["with_reviews_count"], this.local_with_reviews_count), icon: "bi-star-fill", tone: "warning", filter: "with_reviews" },
        { label: "Recent 30 days", value: this.get_dashboard_first(["recent_30_days_count"], this.local_recent_30_count), icon: "bi-calendar-event-fill", tone: "info", filter: "recent_30" },
      ];
    },
    local_published_count() {
      return (this.services || []).filter(service => this.is_published_service(service)).length;
    },
    local_unpublished_count() {
      return this.services.length - this.local_published_count;
    },
    local_recent_30_count() {
      return (this.services || []).filter(service => this.service_created_within_days(service, 30)).length;
    },
    local_with_reviews_count() {
      return (this.services || []).filter(service => this.service_has_reviews(service)).length;
    },
  },
  mounted() {
    this.token_exists = get_token();
    this.user_details = get_user_details() || { user_role: "" };

    if (this.is_admin) {
      this.fetch_all_services();
    }
  },
  methods: {
    get_token() {
      return get_token();
    },
    toggleSidebar() {
      this.$refs.sidebar?.$el?.classList.toggle("close");
    },
    initDataTable(id) {
      const table = $(id);

      if (!table.length) return;

      if ($.fn.DataTable.isDataTable(id)) {
        table.DataTable().destroy();
      }

      table.DataTable({
        paging: true,
        searching: true,
        ordering: true,
        info: true,
        lengthChange: true,
        pageLength: 10,
        language: {
          paginate: {
            previous: "Previous",
            next: "Next",
          },
          info: "Showing _START_ to _END_ of _TOTAL_ entries",
          infoEmpty: "No entries available",
        },
      });
    },
    refresh_services_table() {
      this.$nextTick(() => {
        this.initDataTable("#servicesTable");
      });
    },
    destroy_services_table() {
      if ($.fn.DataTable.isDataTable("#servicesTable")) {
        $("#servicesTable").DataTable().destroy();
      }
    },
    fetch_all_services() {
      this.is_loading_services = true;
      $.ajax({
        url: `${process.env.VUE_APP_BACKEND_API_URL}/ads_management/get_all_services/`,
        method: "GET",
        headers: {
          Authorization: `Token ${this.get_token()}`,
          "Content-Type": "application/json",
        },
        success: (response) => this.handle_services_success(response),
        error: (response) => {
          this.is_loading_services = false;
          this.show_error(response, "An error occurred while loading services.");
        },
      });
    },
    handle_services_success(response) {
      this.is_loading_services = false;
      if (response.status !== "success") return;

      this.destroy_services_table();
      this.set_service_dashboard(response);
      this.set_services(response.data);
      this.refresh_services_table();
    },
    set_service_dashboard(response) {
      this.service_dashboard = Object.assign({}, response.dashboard || {}, {
        data_count: response.data_count,
        total_count: response.count ?? response.dashboard?.total_count,
      });
    },
    set_services(services) {
      this.services = (services || []).filter(service => service?.id);
    },
    handle_service_dashboard_card_click(card) {
      this.destroy_services_table();
      this.service_dashboard_filter = card.filter || "all";
      this.refresh_services_table();
    },
    is_service_dashboard_card_active(card) {
      return this.service_dashboard_filter === (card.filter || "all");
    },
    view_service_details(service) {
      view_profile_product(this.$router, service);
    },
    get_service_profile_name(service) {
      return service?.base_profile?.company?.company_name ||
        [service?.base_profile?.user?.first_name, service?.base_profile?.user?.last_name].filter(Boolean).join(" ") ||
        "--";
    },
    is_published_service(service) {
      const value = service?.is_published;
      return value === true || value === "true";
    },
    service_has_images(service) {
      return Boolean(
        service?.images?.length ||
        service?.files?.length
      );
    },
    service_has_reviews(service) {
      return Boolean(
        Number(service?.total_reviews || 0) > 0 ||
        service?.reviews?.length
      );
    },
    service_created_within_days(service, days) {
      const created_at = service?.created_at;
      if (!created_at) return false;

      const created_date = new Date(created_at);
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - days);

      return created_date >= cutoff;
    },
    get_dashboard_first(keys, fallback = 0) {
      const key = keys.find((dashboard_key) =>
        ![undefined, null, ""].includes(Reflect.get(this.service_dashboard || {}, dashboard_key))
      );
      const value = Reflect.get(this.service_dashboard || {}, key);
      return [undefined, null, ""].includes(value) ? fallback : value;
    },
    show_error(response, fallback) {
      const response_data = response?.responseJSON || {};
      Swal.fire({
        icon: "error",
        title: "Error",
        text: response_data.message || fallback,
        showConfirmButton: false,
        timer: 3000,
      });
    },
  },
};
